import useLocalization from "@/hooks/useLocalization";
import { Product, Venue } from "@/graphql/operations";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { alpha, SxProps, Theme } from "@mui/material";
import { GRAPH_SCOPE_OPTIONS, VIEW_MODE_OPTIONS } from "../constants";
import { DashboardGraphScope, DashboardViewMode } from "../types";
import DashboardGrid from "./DashboardGrid";

type DashboardFiltersProps = {
  graphScope: DashboardGraphScope;
  onGraphScopeChange: (graphScope: DashboardGraphScope) => void;
  onProductChange: (productId: string | null) => void;
  onVenueChange: (venueId: string | null) => void;
  onViewModeChange: (viewMode: DashboardViewMode) => void;
  productId: string | null;
  products: Product[];
  venueId: string | null;
  venues: Venue[];
  viewMode: DashboardViewMode;
};

const toggleGroupSx: SxProps<Theme> = {
  height: 56,
  "& .MuiToggleButton-root": {
    flex: 1,
    textTransform: "none",
  },
  "& .MuiToggleButton-root.Mui-selected": {
    backgroundColor: "primary.main",
    borderColor: "primary.main",
    color: "primary.contrastText",
    fontWeight: 700,
    "&:hover": {
      backgroundColor: "primary.dark",
    },
  },
  "& .MuiToggleButton-root.Mui-selected + .MuiToggleButton-root": {
    borderLeftColor: "primary.main",
  },
  "& .MuiToggleButton-root:not(.Mui-selected):hover": {
    backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.08),
  },
};

export default function DashboardFilters({
  graphScope,
  onGraphScopeChange,
  onProductChange,
  onVenueChange,
  onViewModeChange,
  productId,
  products,
  venueId,
  venues,
  viewMode,
}: DashboardFiltersProps) {
  const { t } = useLocalization();

  return (
    <DashboardGrid>
      <FormControl sx={{ width: "100%" }}>
        <InputLabel>{t("nouns:venue")}</InputLabel>
        <Select
          aria-label={t("command:selectWhat", {
            interpolation: { what: t("nouns:venue") },
          })}
          label={t("nouns:venue")}
          onChange={({ target }) => onVenueChange(target.value || null)}
          value={venueId ?? ""}
        >
          <MenuItem value="" sx={{ opacity: 0.5 }}>
            <em>{t("nouns:all")}</em>
          </MenuItem>
          {venues.map((venue) => (
            <MenuItem key={venue.id} value={venue.id}>
              {venue.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <FormControl sx={{ width: "100%" }}>
        <InputLabel>{t("nouns:tour")}</InputLabel>
        <Select
          aria-label={t("command:selectWhat", {
            interpolation: { what: t("nouns:tour") },
          })}
          label={t("nouns:tour")}
          onChange={({ target }) => onProductChange(target.value || null)}
          value={productId ?? ""}
        >
          <MenuItem value="" sx={{ opacity: 0.5 }}>
            <em>{t("nouns:all")}</em>
          </MenuItem>
          {products.map((product) => (
            <MenuItem key={product.id} value={product.id}>
              {product.name}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <ToggleButtonGroup
        aria-label={t("dashboard:graphScope")}
        color="primary"
        exclusive
        fullWidth
        onChange={(_, nextValue: DashboardGraphScope | null) => {
          if (nextValue === null) return;

          onGraphScopeChange(nextValue);
        }}
        size="large"
        sx={toggleGroupSx}
        value={graphScope}
      >
        {GRAPH_SCOPE_OPTIONS.map(({ labelKey, scope }) => (
          <ToggleButton key={scope} value={scope}>
            {t(`dashboard:${labelKey}` as const)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>

      <ToggleButtonGroup
        aria-label={t("dashboard:viewMode")}
        color="primary"
        exclusive
        fullWidth
        onChange={(_, nextValue: DashboardViewMode | null) => {
          if (nextValue === null) return;

          onViewModeChange(nextValue);
        }}
        size="large"
        sx={toggleGroupSx}
        value={viewMode}
      >
        {VIEW_MODE_OPTIONS.map(({ labelKey, viewMode: optionViewMode }) => (
          <ToggleButton key={optionViewMode} value={optionViewMode}>
            {t(`dashboard:${labelKey}` as const)}
          </ToggleButton>
        ))}
      </ToggleButtonGroup>
    </DashboardGrid>
  );
}
